type CanonicalizeLeadUrlInput = {
  currentAsPath: string | undefined | null;
  targetRawQuery: string | undefined | null;
  routerReplace: (target: string) => void;
};

const splitHash = (
  asPath: string
): { withoutHash: string; hash: string } => {
  const hashIndex = asPath.indexOf('#');
  return hashIndex >= 0
    ? { withoutHash: asPath.slice(0, hashIndex), hash: asPath.slice(hashIndex) }
    : { withoutHash: asPath, hash: '' };
};

const safeDecode = (value: string): string => {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
};

// decodeURIComponent (not URLSearchParams) because rawQuery values are
// concatenated raw by getRawQuery — URLSearchParams would treat a literal `+`
// as space and diverge from asPath's `%2B` percent-encoding.
const getQuerySignature = (queryString: string): string => {
  if (!queryString) return '[]';
  const entries: Array<[string, string]> = queryString
    .split('&')
    .filter(Boolean)
    .map((pair) => {
      const eqIndex = pair.indexOf('=');
      const rawKey = eqIndex >= 0 ? pair.slice(0, eqIndex) : pair;
      const rawValue = eqIndex >= 0 ? pair.slice(eqIndex + 1) : '';
      return [safeDecode(rawKey), safeDecode(rawValue)];
    });
  entries.sort(([ka, va], [kb, vb]) =>
    ka === kb ? va.localeCompare(vb) : ka.localeCompare(kb)
  );
  return JSON.stringify(entries);
};

export const areLeadQueriesEquivalent = (
  currentAsPath: string | undefined | null,
  targetRawQuery: string | undefined | null
): boolean => {
  const { withoutHash } = splitHash(currentAsPath || '');
  const questionMarkIndex = withoutHash.indexOf('?');
  const currentQueryString =
    questionMarkIndex >= 0 ? withoutHash.slice(questionMarkIndex + 1) : '';

  const rawQuery = targetRawQuery || '';
  const targetQueryString = rawQuery.startsWith('?')
    ? rawQuery.slice(1)
    : rawQuery;

  return (
    getQuerySignature(currentQueryString) ===
    getQuerySignature(targetQueryString)
  );
};

export const canonicalizeLeadUrl = ({
  currentAsPath,
  targetRawQuery,
  routerReplace,
}: CanonicalizeLeadUrlInput): void => {
  if (areLeadQueriesEquivalent(currentAsPath, targetRawQuery)) return;

  const { hash } = splitHash(currentAsPath || '');
  const rawQuery = targetRawQuery || '';

  routerReplace(`/lead${rawQuery}${hash}`);
};
